import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { reflectionApi } from '@/services/learningManagementApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Edit, Trash2, Brain } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Reflection } from '@/types/learningManagement';

export const ReflectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'reflection');
  const canDelete = hasPermission('delete', 'reflection');

  // Use entity hook for fetching reflection
  const {
    data: reflection,
    isLoading,
    error,
    refetch,
  } = useEntity<Reflection>({
    fetchFn: (reflectionId: string) => reflectionApi.getById(reflectionId),
    entityId: id,
    entityName: 'reflection',
    enabled: !!id,
  });

  const isSystemDefined = reflection?.reflectionGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'reflection'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Reflection ID is required');
      await reflectionApi.delete(id);
    },
    entityName: 'reflection',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/reflections';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this reflection?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading reflection..." />;
  }

  if (error || !reflection) {
    return <ErrorState error={error || new Error('Reflection not found')} onRetry={refetch} />;
  }

  const reflectionId = reflection.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/reflections">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Brain className="h-5 w-5" />
              View Reflection
            </h2>
            <p className="text-muted-foreground mt-1">
              {reflection.skillId ? `Skill: ${reflection.skillId}` : 'Reflection Details'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/reflections/${reflectionId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList>
          <TabsTrigger value="basic">Basic Information</TabsTrigger>
          <TabsTrigger value="details">Reflection Details</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Reflection identification and classification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <CopyableId id={reflectionId} label="Reflection ID" />
                {reflection.skillId && (
                  <CopyableId id={reflection.skillId} label="Skill ID" />
                )}
              </div>

              {reflection.timeInvestedHours !== undefined && (
                <div className="space-y-2">
                  <Label>Time Invested (Hours)</Label>
                  <Input value={reflection.timeInvestedHours.toString()} disabled />
                </div>
              )}

              {reflection.skillLevelImprovement && (
                <div className="space-y-2">
                  <Label>Skill Level Improvement</Label>
                  <Input value={reflection.skillLevelImprovement.replace(/_/g, ' ')} disabled />
                </div>
              )}

              {reflection.deliveryFrequency && (
                <div className="space-y-2">
                  <Label>Delivery Frequency</Label>
                  <Input value={reflection.deliveryFrequency.replace(/_/g, ' ')} disabled />
                </div>
              )}

              {reflection.deliveryChannels && reflection.deliveryChannels.length > 0 && (
                <div className="space-y-2">
                  <Label>Delivery Channels</Label>
                  <div className="flex flex-wrap gap-2">
                    {reflection.deliveryChannels.map((channel, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm"
                      >
                        {channel.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Generation Source</Label>
                <Input value={reflection.reflectionGenerated} disabled />
              </div>

              {reflection.createdAt && (
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Created At</Label>
                    <Input value={new Date(reflection.createdAt).toLocaleString()} disabled />
                  </div>
                  {reflection.updatedAt && (
                    <div className="space-y-2">
                      <Label>Updated At</Label>
                      <Input value={new Date(reflection.updatedAt).toLocaleString()} disabled />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Reflection Details</CardTitle>
              <CardDescription>Detailed reflection information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {reflection.reflectionNotes && (
                <div className="space-y-2">
                  <Label>Reflection Notes</Label>
                  <textarea
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                    value={reflection.reflectionNotes}
                    disabled
                  />
                </div>
              )}

              {reflection.improvementAreas && reflection.improvementAreas.length > 0 && (
                <div className="space-y-2">
                  <Label>Improvement Areas</Label>
                  <div className="space-y-2">
                    {reflection.improvementAreas.map((area, idx) => (
                      <div key={idx} className="p-3 rounded-md bg-secondary text-secondary-foreground">
                        {area}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reflection.nextWeekSkillGoals && reflection.nextWeekSkillGoals.length > 0 && (
                <div className="space-y-2">
                  <Label>Next Week Skill Goals</Label>
                  <div className="space-y-2">
                    {reflection.nextWeekSkillGoals.map((goal, idx) => (
                      <div key={idx} className="p-3 rounded-md bg-secondary text-secondary-foreground">
                        {goal}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reflection.practicalApplications && reflection.practicalApplications.length > 0 && (
                <div className="space-y-2">
                  <Label>Practical Applications</Label>
                  <div className="space-y-2">
                    {reflection.practicalApplications.map((app, idx) => (
                      <div key={idx} className="p-3 rounded-md bg-secondary text-secondary-foreground">
                        {app}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reflection.resourcesUsed && reflection.resourcesUsed.length > 0 && (
                <div className="space-y-2">
                  <Label>Resources Used</Label>
                  <div className="space-y-2">
                    {reflection.resourcesUsed.map((resource, idx) => (
                      <div key={idx} className="p-3 rounded-md bg-secondary text-secondary-foreground">
                        <div className="font-medium">{resource.resourceTitle || resource.resourceId || 'Resource'}</div>
                        {resource.resourceUrl && (
                          <div className="text-sm text-muted-foreground mt-1">{resource.resourceUrl}</div>
                        )}
                        {resource.resourceType && (
                          <div className="text-sm text-muted-foreground">Type: {resource.resourceType}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reflection.targetAudiences && reflection.targetAudiences.length > 0 && (
                <div className="space-y-2">
                  <Label>Target Audiences</Label>
                  <div className="flex flex-wrap gap-2">
                    {reflection.targetAudiences.map((audience, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm"
                      >
                        {audience.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReflectionDetailPage;






